import { useEffect, useState } from 'react';

// The campaign the user has active, for pages that follow it (the Equipment page and the
// Bestiary offer only its game system's lists). Read again when another view switches it
// (the window's `activeCampaignChanged` event).

/** The backend's `active` flag first (it follows the user across devices), then this browser's
 *  last pick, then the first - the same order as the Header. */
export function pickActiveCampaign(campaigns, username) {
    let localId = null;
    try { localId = JSON.parse(localStorage.getItem(`selectedCampaignId_${username}`) || 'null'); } catch {}
    return campaigns.find(c => c.active) ?? campaigns.find(c => c.id === localId) ?? campaigns[0] ?? null;
}

/** Makes a campaign the user's active one everywhere: this browser's pick, the backend's flag (the
 *  one pickActiveCampaign trusts first) and the window's `activeCampaignChanged` event. The flag
 *  is awaited, so a page opened next (the tabletop) already reads it - a local pick alone loses
 *  to the old flag there. Resolves true once the backend has it, false if that call failed. */
export async function switchActiveCampaign(campaign) {
    let userJSON = {};
    try { userJSON = JSON.parse(sessionStorage.getItem('user') || '{}') || {}; } catch {}
    try { localStorage.setItem(`selectedCampaignId_${userJSON.username}`, JSON.stringify(campaign.id)); } catch {}
    let saved = false;
    try {
        const res = await fetch(`${process.env.REACT_APP_API_PROXY}/campaigns/setActiveCampaign/${campaign.id}`, {
            method: 'POST',
            headers: { Authorization: 'Bearer ' + userJSON.accessToken },
        });
        saved = res.ok;
    } catch (err) {
        console.error('Failed to persist active campaign', err);
    }
    const party = campaign.party ?? campaign.parties?.[0] ?? null;
    window.dispatchEvent(new CustomEvent('activeCampaignChanged', {
        detail: { campaignName: campaign.campaignName, partyName: party?.name ?? '' },
    }));
    return saved;
}

/** { campaign, loaded }: the active campaign (null if none), and whether it has been read yet. */
export function useActiveCampaign() {
    const [state, setState] = useState({ campaign: null, loaded: false });
    const [version, setVersion] = useState(0);
    useEffect(() => {
        const bump = () => setVersion(v => v + 1);
        window.addEventListener('activeCampaignChanged', bump);
        return () => window.removeEventListener('activeCampaignChanged', bump);
    }, []);
    useEffect(() => {
        let userJSON = {};
        try { userJSON = JSON.parse(sessionStorage.getItem('user') || '{}') || {}; } catch {}
        if (!userJSON.id) { setState({ campaign: null, loaded: true }); return undefined; }
        let current = true;
        fetch(`${process.env.REACT_APP_API_PROXY}/campaigns/getAllCampaignsForUser`, {
            headers: { Authorization: 'Bearer ' + userJSON.accessToken },
        })
            .then(r => (r.ok ? r.json() : []))
            .then(campaigns => {
                if (current) setState({ campaign: pickActiveCampaign(Array.isArray(campaigns) ? campaigns : [], userJSON.username), loaded: true });
            })
            .catch(() => { if (current) setState({ campaign: null, loaded: true }); });
        return () => { current = false; };
    }, [version]);
    return state;
}
